import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../environments/environment';
import { AuthService } from '../core/auth.service';
import { LanguageService } from '../core/language.service';
import { buildPortalUrlForKey, resolvePortalHomeUrl } from '../core/portal-redirect.util';

@Component({
  standalone: true,
  selector: 'app-login',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatTooltipModule,
    TranslateModule,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private auth = inject(AuthService);
  private formBuilder = inject(FormBuilder);
  lang = inject(LanguageService);

  authForm!: FormGroup;
  submitted = false;
  loading = false;
  rememberMe = false;
  error = '';
  hide = true;
  capsLockOn = false;

  /** أثناء التحقق من كوكي الجلسة على API (منفذ مختلف عن الواجهة) */
  sessionCheckPending = true;

  // ممكن يكون /connect/authorize?...
  continueUrl: string | null = null;

  ngOnInit() {
    this.continueUrl = this.route.snapshot.queryParamMap.get('continue');
    const gateError = this.route.snapshot.queryParamMap.get('error');
    if (gateError === 'access_denied') {
      this.error =
        'تم رفض الوصول (مثلاً: طلب سكوب تأمين دون ربط شركة في المختبر). راجع المسؤول أو أعد المحاولة.';
    }

    // Initialize form
    this.authForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
    });

    // Load saved username and rememberMe state if exists
    const savedUsername = localStorage.getItem('remembered_username');
    const savedRememberMe = localStorage.getItem('remember_me') === 'true';
    
    if (savedUsername && savedRememberMe) {
      this.authForm.get('username')?.setValue(savedUsername);
      this.rememberMe = true;
    }

    void this.tryResumeExistingSession();
  }

  /**
   * إذا كانت هناك جلسة كوكي على خادم المصادقة (5002) لكن الصفحة فُتحت على واجهة Angular (52395)،
   * أعد التوجيه إلى continue (OAuth) أو للرئيسية دون طلب كلمة المرور مرة أخرى.
   *
   * Loop guard: لو returnnا للوغين بنفس الـ continueUrl خلال 10 ثوان لأكثر من
   * مرتين، نوقف الـ auto-redirect ونعرض نموذج الدخول. هذا يمنع لوب أبدي يصير
   * لما الـ Lab يفتح بدون authToken (sessionStorage فاضي بتاب جديد) ويرجع.
   */
  private async tryResumeExistingSession(): Promise<void> {
    if (this.detectAndBreakRedirectLoop()) {
      this.sessionCheckPending = false;
      this.error = 'تعذّر إكمال تسجيل الدخول التلقائي. الرجاء إدخال بيانات الاعتماد.';
      return;
    }

    try {
      const resp = await firstValueFrom(
        this.http.get<{ authenticated: boolean }>(`${environment.apiBase}/account/session-status`, {
          withCredentials: true,
        })
      );
      if (resp?.authenticated) {
        const next = this.continueUrl;
        if (next) {
          // If we are about to forward to an external app and we have no
          // access token to attach, the destination will just bounce us back
          // → infinite loop. Show the form instead and let the user re-auth.
          const absolute = this.resolveContinueUrl(next);
          const willGoExternal = this.isExternalAppUrl(absolute);
          if (willGoExternal && !this.auth.accessToken) {
            this.sessionCheckPending = false;
            this.error = 'الجلسة نشطة لكن لا يوجد توكن في هذا التبويب. سجّل الدخول لإكمال الانتقال.';
            return;
          }
          const finalUrl = this.appendTokenForExternalApp(absolute);
          this.markRedirectAttempt(next);
          window.location.href = finalUrl;
          // اترك sessionCheckPending true حتى يُحمّل authorize (تجنب وميض النموذج)
          return;
        }
        // ميّز وجهة المستخدم من الخادم (الرتبة/الارتباط) عند توفّر التوكن —
        // متّسق مع تسجيل الدخول والحارس، ولا يعتمد على سكوبات التوكن.
        if (this.auth.accessToken) {
          try {
            const target = await firstValueFrom(
              this.http.get<{ portalKey?: string | null }>(
                `${environment.apiBase}/api/account/portal-target`,
                { withCredentials: true }
              )
            );
            const portalUrl = buildPortalUrlForKey(target?.portalKey, this.auth.accessToken);
            if (portalUrl) {
              window.location.href = portalUrl;
              return;
            }
            // مستخدم مركزي (موظف/أدمن) → صفحة التطبيقات.
            this.sessionCheckPending = false;
            await this.router.navigateByUrl('/');
            return;
          } catch {
            // التوكن غير صالح/منتهٍ — لا نثق به؛ اعرض نموذج الدخول بدل احتجاز المستخدم.
            this.sessionCheckPending = false;
            return;
          }
        }
        // جلسة كوكي بلا توكن في هذا التبويب — اعرض نموذج الدخول لإكمال الدخول.
        this.sessionCheckPending = false;
        return;
      }

      // الخادم يقول «غير مُصادق» (مثلاً بعد تسجيل الخروج): امسح أي توكن يتيم في
      // هذا الأصل. بدونه يبقى توكن مرآة localStorage (المنشأ 52395) حيّاً بعد خروج
      // أُطلق من بوابة أخرى (المنشأ 4201)، فيظهر المستخدم «مسجّلاً» ويُعاد إدخاله.
      this.auth.clearLocalSessionOnly();
    } catch {
      // تجاهل — نعرض نموذج الدخول
    }
    this.sessionCheckPending = false;
  }

  /** Returns true if we've redirected with the same continue URL >=2 times in the last 10s. */
  private detectAndBreakRedirectLoop(): boolean {
    if (!this.continueUrl) return false;
    try {
      const KEY = 'auth.loginRedirectGuard';
      const WINDOW_MS = 10_000;
      const MAX = 2;
      const raw = sessionStorage.getItem(KEY);
      const now = Date.now();
      const state = raw ? JSON.parse(raw) : null;
      if (state && state.url === this.continueUrl && now - state.firstAt < WINDOW_MS) {
        if (state.count >= MAX) {
          sessionStorage.removeItem(KEY);
          return true;
        }
      }
    } catch { /* sessionStorage disabled */ }
    return false;
  }

  private markRedirectAttempt(continueUrl: string): void {
    try {
      const KEY = 'auth.loginRedirectGuard';
      const WINDOW_MS = 10_000;
      const raw = sessionStorage.getItem(KEY);
      const now = Date.now();
      let state = raw ? JSON.parse(raw) : null;
      if (!state || state.url !== continueUrl || now - state.firstAt > WINDOW_MS) {
        state = { url: continueUrl, count: 1, firstAt: now };
      } else {
        state.count++;
      }
      sessionStorage.setItem(KEY, JSON.stringify(state));
    } catch { /* sessionStorage disabled */ }
  }

  private isExternalAppUrl(next: string): boolean {
    try {
      const nextLower = next.trim().toLowerCase();
      if (!nextLower.startsWith('http://') && !nextLower.startsWith('https://')) return false;
      const nextUrl = new URL(next);
      const authOrigin = window.location.origin;
      const apiOrigin = new URL(environment.apiBase).origin;
      return nextUrl.origin !== authOrigin && nextUrl.origin !== apiOrigin;
    } catch {
      return false;
    }
  }

  get f() {
    return this.authForm.controls;
  }

  /**
   * لوب سينمائي لفيديو الهوية: عند انتهاء الفيديو نثبّت على اللقطة الأخيرة (الشعار الكامل)
   * لثانيتين، ثم نعمل تلاشي ناعم ونعيد التشغيل من البداية — بدل قفزة اللوب المفاجئة.
   */
  onBrandVideoEnded(video: HTMLVideoElement) {
    setTimeout(() => {
      video.classList.add('video-fading');
      setTimeout(() => {
        video.currentTime = 0;
        void video.play();
        video.classList.remove('video-fading');
      }, 500);
    }, 2200);
  }

  onRememberMeChange() {
    // If user unchecks remember me, clear saved data
    if (!this.rememberMe) {
      localStorage.removeItem('remembered_username');
      localStorage.removeItem('remember_me');
    }
  }

  /** يظهر تنبيهاً تحت حقل كلمة المرور عندما يكون Caps Lock مفعّلاً */
  onPasswordKeyEvent(event: KeyboardEvent) {
    this.capsLockOn = event.getModifierState?.('CapsLock') ?? false;
  }

  forgotPassword() {
    window.alert('تواصل مع الدعم لإعادة التعيين.');
  }

  /**
   * مسارات العودة من السيرفر (cura_continue) نسبية لـ API مثل /connect/authorize — يجب فتحها على apiBase وليس على origin الواجهة.
   */
  private resolveContinueUrl(next: string): string {
    const t = next.trim();
    if (!t) return t;
    if (t.startsWith('http://') || t.startsWith('https://')) return t;
    const base = environment.apiBase.replace(/\/+$/, '');
    return t.startsWith('/') ? base + t : `${base}/${t}`;
  }

  /**
   * إذا كان رابط continue يُشير إلى تطبيق خارجي (Cura Lab)، أضف authToken ليعود المستخدم مُسجّل الدخول.
   * لا نضيف التوكن لروابط سيرفر المصادقة (/connect/authorize إلخ) — الكوكي كافٍ لإكمال OAuth.
   */
  private appendTokenForExternalApp(next: string): string {
    try {
      const nextLower = next.trim().toLowerCase();
      if (!nextLower.startsWith('http://') && !nextLower.startsWith('https://')) {
        return next;
      }
      const nextUrl = new URL(next);
      const authOrigin = window.location.origin;
      const apiOrigin = new URL(environment.apiBase).origin;
      if (nextUrl.origin === apiOrigin) {
        return next;
      }
      if (nextUrl.origin !== authOrigin) {
        const token = this.auth.accessToken;
        if (token) {
          const sep = next.includes('?') ? '&' : '?';
          return next + sep + 'authToken=' + encodeURIComponent(token);
        }
      }
    } catch (_) {}
    return next;
  }

  async onSubmit() {
    this.submitted = true;
    this.loading = true;
    this.error = '';

    // Manual login wipes any prior redirect-loop tracker.
    try { sessionStorage.removeItem('auth.loginRedirectGuard'); } catch {}

    if (this.authForm.invalid) {
      this.error = 'Username and Password not valid !';
      this.loading = false;
      return;
    }

    const username = this.f['username'].value;
    const password = this.f['password'].value;

    try {
      // 1) أنشئ كوكي المصادقة في السيرفر
      const resp = await this.http
        .post<{ continue: string; portalKey?: string | null }>(`${environment.apiBase}/account/login`, {
          username: username,
          password: password,
          rememberMe: this.rememberMe,
        })
        .toPromise();

      // 2) احصل على JWT للـ SPA (Password Grant) وخزّنه
      //    (حتى لو في continue، نعملها بسرعة قبل إعادة التوجيه)
      await this.auth.loginWithPassword(
        username,
        password,
        'AngularSPA',
        this.rememberMe
      );

      // Handle Remember Me functionality
      if (this.rememberMe) {
        localStorage.setItem('remembered_username', username);
        localStorage.setItem('remember_me', 'true');
      } else {
        localStorage.removeItem('remembered_username');
        localStorage.removeItem('remember_me');
      }

      // 3) قرّر أين تذهب:
      // أولوية وجهة OAuth الحقيقية (authorize / تطبيق خارجي) لإكمال SSO؛
      // القيمتان "" و"/" تعنيان «لا وجهة» (دخول مباشر على البوابة).
      const continueTarget = (this.continueUrl || resp?.continue || '').trim();
      const hasRealContinue = continueTarget !== '' && continueTarget !== '/';
      if (hasRealContinue) {
        const absolute = this.resolveContinueUrl(continueTarget);
        const finalUrl = this.appendTokenForExternalApp(absolute);
        window.location.href = finalUrl;
        return;
      }

      // مستخدم بوابة خارجية حسب قرار الخادم (الرتبة/الارتباط) → بوابته مباشرةً،
      // ولا يرى البوابة المركزية. fallback: استنتاج من التوكن، ثم صفحة التطبيقات.
      const portalUrl =
        buildPortalUrlForKey(resp?.portalKey, this.auth.accessToken) ??
        resolvePortalHomeUrl(this.auth.claims, this.auth.accessToken);
      if (portalUrl) {
        window.location.href = portalUrl;
      } else {
        this.router.navigateByUrl('/');
      }
    } catch (e: any) {
      this.loading = false;
      this.submitted = false;

      const err = e?.error || e;
      if (err?.error === 'password_change_required') {
        const userId = err?.userId;
        if (userId) {
          this.router.navigate(['/force-change-password'], {
            state: { userId, username, currentPassword: password },
          });
          return;
        }
        this.error = 'يجب تغيير كلمة المرور قبل المتابعة.';
      } else if (err?.error === 'session_limit_reached') {
        this.error = 'تم بلوغ الحد الأقصى للجلسات المتزامنة.';
      } else if (err?.error === 'invalid_credentials' || err?.error === 'invalid_grant') {
        this.error = 'بيانات الدخول غير صحيحة.';
      } else {
        this.error = e?.message || 'تعذر تسجيل الدخول. حاول لاحقًا.';
      }
    }
  }
}
